import {
  startOfWeek,
  endOfWeek,
  format,
  parseISO,
  addWeeks,
  subWeeks,
  isSameWeek,
  startOfDay,
  endOfDay,
  isWithinInterval,
  compareAsc,
  isSameDay,
  set,
  addMinutes,
} from 'date-fns';

/**
 * Constants for time and duration selection
 */
export const DURATION_OPTIONS = [1, 2, 3, 4, 5, 6, 8] as const;
export const CUSTOM_DURATION_VALUE = 'custom' as const;

/**
 * Get the start and end dates of a week containing the given date
 */
export function getWeekRange(date: Date) {
  const start = startOfWeek(date, { weekStartsOn: 0 }); // Sunday
  const end = endOfWeek(date, { weekStartsOn: 0 }); // Saturday

  return { start, end };
}

/**
 * Format a date range for display (e.g., "Nov 11-17, 2025")
 */
export function formatWeekRange(start: Date, end: Date): string {
  const startMonth = format(start, 'MMM');
  const endMonth = format(end, 'MMM');
  const startDay = format(start, 'd');
  const endDay = format(end, 'd');
  const year = format(end, 'yyyy');

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}-${endDay}, ${year}`;
  } else {
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  }
}

/**
 * Get the next week's start date
 */
export function getNextWeek(currentWeekStart: Date): Date {
  return addWeeks(currentWeekStart, 1);
}

/**
 * Get the previous week's start date
 */
export function getPreviousWeek(currentWeekStart: Date): Date {
  return subWeeks(currentWeekStart, 1);
}

/**
 * Check if a date is in the current week
 */
export function isInCurrentWeek(date: Date, weekStart: Date): boolean {
  return isSameWeek(date, weekStart, { weekStartsOn: 0 });
}

/**
 * Format time in 12-hour format (e.g., "2:30 PM")
 */
export function formatTime(dateString: string): string {
  return format(parseISO(dateString), 'h:mm a');
}

/**
 * Format a time rannge into a compact string.
 * Example: 4:00 PM to 9:00 PM -> "4-9pm"
 * Example: 4:30 PM to 9:00 PM -> "4:30-9pm"
 * Example: 10:00 AM to 2:00 PM -> "10am-2pm"
 */
export function formatCompactTimeRange(start: Date | string, end: Date | string): string {
  const startDate = start instanceof Date ? start : parseISO(start);
  const endDate = end instanceof Date ? end : parseISO(end);

  const startHours = startDate.getHours();
  const startMinutes = startDate.getMinutes();
  const startMeridiem = startHours >= 12 ? 'pm' : 'am';
  const startHour12 = startHours % 12 || 12;
  const startClock =
    startMinutes === 0
      ? `${startHour12}`
      : `${startHour12}:${String(startMinutes).padStart(2, '0')}`;

  const endHours = endDate.getHours();
  const endMinutes = endDate.getMinutes();
  const endMeridiem = endHours >= 12 ? 'pm' : 'am';
  const endHour12 = endHours % 12 || 12;
  const endClock =
    endMinutes === 0 ? `${endHour12}` : `${endHour12}:${String(endMinutes).padStart(2, '0')}`;

  if (startMeridiem === endMeridiem) {
    return `${startClock}-${endClock}${endMeridiem}`;
  }

  return `${startClock}${startMeridiem}-${endClock}${endMeridiem}`;
}

/**
 * Format date for display (e.g., "Monday, Nov 11")
 */
export function formatDateDisplay(dateString: string): string {
  return format(parseISO(dateString), 'EEEE, MMM d');
}

/**
 * Group availability slots by day
 */
export function groupSlotsByDay<T extends { startTime: string }>(slots: T[]): Record<string, T[]> {
  const grouped: Record<string, T[]> = {};

  slots.forEach(slot => {
    const date = format(parseISO(slot.startTime), 'yyyy-MM-dd');
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(slot);
  });

  // Sort slots within each day by start time
  Object.keys(grouped).forEach(date => {
    grouped[date].sort((a, b) => compareAsc(parseISO(a.startTime), parseISO(b.startTime)));
  });

  return grouped;
}

/**
 * Filter slots for a specific week
 */
export function filterSlotsForWeek<T extends { startTime: string }>(
  slots: T[],
  weekStart: Date
): T[] {
  const { start, end } = getWeekRange(weekStart);

  return slots.filter(slot => {
    const slotDate = parseISO(slot.startTime);
    return isWithinInterval(slotDate, { start: startOfDay(start), end: endOfDay(end) });
  });
}

/**
 * Get days of the week for display
 */
export function getWeekDays(weekStart: Date): Date[] {
  const days: Date[] = [];
  const { start } = getWeekRange(weekStart);

  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    days.push(day);
  }

  return days;
}

/**
 * Generate time slot options for dropdowns in 15-minute intervals
 */
export function generateTimeSlots(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      // Create a date object for formatting
      const time = new Date();
      time.setHours(hour, minute, 0, 0);

      const value = format(time, 'HH:mm');
      const label = format(time, 'h:mm a');
      options.push({ value, label });
    }
  }

  return options;
}

/**
 * Filter time slots to only show times after a given start time
 */
export function filterTimeSlotsAfter(
  timeSlots: { value: string; label: string }[],
  startTime: string
): { value: string; label: string }[] {
  if (!startTime) return timeSlots;

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const startTotalMinutes = startHour * 60 + startMinute;

  return timeSlots.filter(option => {
    const [hour, minute] = option.value.split(':').map(Number);
    const totalMinutes = hour * 60 + minute;
    return totalMinutes > startTotalMinutes;
  });
}

/**
 * Get default start and end times for a given date.
 * If the date is today, it suggests the next available 15-minute block.
 * Otherwise, it defaults to 9:00 AM.
 */
export function getDefaultTimes(date: Date) {
  const now = new Date();
  const isToday = isSameDay(now, date);

  if (!isToday) {
    const defaultStart = set(new Date(date), {
      hours: 9,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });
    return {
      startTime: format(defaultStart, 'HH:mm'),
      endTime: format(addMinutes(defaultStart, 60), 'HH:mm'),
    };
  }

  const minutesFromMidnight = now.getHours() * 60 + now.getMinutes();
  const roundedMinutes = Math.ceil(minutesFromMidnight / 15) * 15;
  const latestStartMinutes = 22 * 60 + 45; // leave room for at least a 1-hour slot
  const safeStartMinutes = Math.min(roundedMinutes, latestStartMinutes);

  const startHour = Math.floor(safeStartMinutes / 60);
  const startMinute = safeStartMinutes % 60;

  const baseTime = set(new Date(date), {
    hours: startHour,
    minutes: startMinute,
    seconds: 0,
    milliseconds: 0,
  });

  const startTime = format(baseTime, 'HH:mm');
  const endTime = format(addMinutes(baseTime, 60), 'HH:mm');

  return { startTime, endTime };
}

/**
 * Validates that endTime is after startTime
 */
export function validateTimeOrder(startTime: string, endTime: string): boolean {
  if (!startTime || !endTime) return true;
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  const startTotalMinutes = startHour * 60 + startMinute;
  const endTotalMinutes = endHour * 60 + endMinute;

  return endTotalMinutes > startTotalMinutes;
}

const DAY_NAME_PLURAL_MAP: Record<string, string> = {
  MONDAY: 'Mondays',
  TUESDAY: 'Tuesdays',
  WEDNESDAY: 'Wednesdays',
  THURSDAY: 'Thursdays',
  FRIDAY: 'Fridays',
  SATURDAY: 'Saturdays',
  SUNDAY: 'Sundays',
};

/**
 * Returns the plural weekday name (e.g. "Wednesdays") from a date or day of week string.
 * Supports a date string, Date object, options object, or DayOfWeek string.
 */
export function getWeekdayPlural(
  dateOrOptions?: string | Date | null | { date?: string | Date | null; dayOfWeek?: string | null },
  fallbackDayOfWeek?: string | null
): string {
  let date: string | Date | null | undefined;
  let dayOfWeek = fallbackDayOfWeek;

  if (dateOrOptions && typeof dateOrOptions === 'object' && !(dateOrOptions instanceof Date)) {
    date = dateOrOptions.date;
    if (dateOrOptions.dayOfWeek !== undefined) {
      dayOfWeek = dateOrOptions.dayOfWeek;
    }
  } else {
    date = dateOrOptions;
  }

  if (date) {
    try {
      if (date instanceof Date) {
        if (!isNaN(date.getTime())) {
          return `${format(date, 'EEEE')}s`;
        }
      } else {
        const cleanDate = date.includes('T') ? date.split('T')[0] : date;
        const parsed = parseISO(cleanDate);
        if (!isNaN(parsed.getTime())) {
          return `${format(parsed, 'EEEE')}s`;
        }
      }
    } catch {
      // ignore
    }
  }

  if (dayOfWeek && typeof dayOfWeek === 'string') {
    const plural = DAY_NAME_PLURAL_MAP[dayOfWeek.toUpperCase()];
    if (plural) {
      return plural;
    }
  }

  return '';
}
